import { useCallback, useEffect, useRef } from 'react'
import { useMarkCommentsAsReadMutation } from '@sketch/gql-types'
import { useFlag } from '@sketch/modules-common'

const MARK_AS_READ_DELAY = 2000
const COMMENT_READ_LIST: Set<string> = new Set()

const useMarkCommentAsRead = () => {
  const commentIdentifiers = useRef<Set<string>>(new Set())
  const setTimeOutRef = useRef<NodeJS.Timeout | null>(null)
  const includeHasUnreadComments = useFlag('pages-unread-annotations')

  const [mutation] = useMarkCommentsAsReadMutation({
    onError: 'show-toast',
  })

  useEffect(() => {
    return () => {
      setTimeOutRef.current && clearTimeout(setTimeOutRef.current)
    }
  }, [])

  const queueMarkAsRead = useCallback(() => {
    const isBatchInProgress = setTimeOutRef.current !== null

    if (isBatchInProgress) {
      return
    }

    setTimeOutRef.current = setTimeout(async () => {
      const unreadCommentIdentifiers: string[] = []

      for (const identifier of commentIdentifiers.current) {
        if (unreadCommentIdentifiers.length >= 40) {
          break
        }

        if (!COMMENT_READ_LIST.has(identifier)) {
          unreadCommentIdentifiers.push(identifier)
        }
      }

      if (unreadCommentIdentifiers.length === 0) {
        // Clear the setTimeOut ref
        setTimeOutRef.current = null
        return
      }

      await mutation({
        variables: {
          commentIdentifiers: unreadCommentIdentifiers,
          includeHasUnreadComments,
        },
        update: () => {
          unreadCommentIdentifiers.forEach(identifier => {
            COMMENT_READ_LIST.add(identifier)
            commentIdentifiers.current.delete(identifier)
          })

          // Clear the setTimeOut ref
          setTimeOutRef.current = null

          if (commentIdentifiers.current.size > 0) {
            queueMarkAsRead()
          }
        },
      })
    }, MARK_AS_READ_DELAY)
  }, [mutation, includeHasUnreadComments])

  return useCallback(
    (identifiers: string[]) => {
      identifiers.forEach(identifier => {
        commentIdentifiers.current.add(identifier)
      })
      queueMarkAsRead()
    },
    [queueMarkAsRead]
  )
}

export default useMarkCommentAsRead
